import React, { useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'

import { splitUrlsIntoArray } from '../../utils/index'

function MultipleTextInput({
    sx,
    initialValues = [],
    setSelectedValues,
    label = 'Text',
    placeholder = 'Type and press enter',
    suggestions = [],
}) {
    const [values, setValues] = useState([])
    // Controlled so the typed-but-uncommitted text can be read on blur.
    const [typedValue, setTypedValue] = useState('')

    React.useEffect(() => {
        setValues(initialValues)
    }, [initialValues])

    const handleInputChange = (event, newValue) => {
        const eraseAllClick =
            event.type == 'click' && newValue && newValue.length == 0
                ? true
                : false

        if (eraseAllClick) {
            setValues([])
            return
        }

        const trimmedValue = newValue.toString().trim()

        const containsSeveralLinks =
            trimmedValue.includes(',http') ||
            trimmedValue.includes('%20http') ||
            trimmedValue.includes(' http')

        if (containsSeveralLinks) {
            const links = splitUrlsIntoArray(trimmedValue)

            const trimmedLinks = links.map(link => link.trim())

            const filteredLinks = trimmedLinks
                .filter(link => link !== '')
                .filter(link => !values.includes(link))
            setValues([...trimmedLinks])

            return
        }

        // Check if the value isn't empty and not already in the array
        if (trimmedValue && !values.includes(trimmedValue)) {
            // Add value to the array
            setValues([...values, trimmedValue])
        }

        if (trimmedValue && values.includes(trimmedValue)) {
            // Remove value from the array
            setValues(values.filter(value => value == trimmedValue))
        }
    }

    /**
     * Commit the typed entry on blur; otherwise it is silently dropped unless the user
     * presses Enter, and the dialog's action stays disabled with nothing on screen saying why.
     */
    const commitTypedValue = () => {
        const trimmed = typedValue.trim()
        if (!trimmed) return
        handleInputChange({ type: 'blur' }, [...values, trimmed])
        setTypedValue('')
    }

    const handleDeleteChip = chipToDelete => () => {
        console.log('chipToDelete', chipToDelete)

        setValues(chips => chips.filter(chip => chip !== chipToDelete))
    }

    React.useEffect(() => {
        setSelectedValues(values)
    }, [values])

    return (
        <Autocomplete
            sx={sx}
            multiple
            freeSolo
            options={suggestions}
            value={values}
            inputValue={typedValue}
            onInputChange={(event, newInputValue) =>
                setTypedValue(newInputValue)
            }
            onChange={(event, newValue) => {
                // newValue contains the latest array after the change (including new chips added by the user's input)
                handleInputChange(event, newValue)
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Chip
                        variant="outlined"
                        label={option}
                        onDelete={() => handleDeleteChip(option)}
                        {...getTagProps({ index })}
                    />
                ))
            }
            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    placeholder={placeholder}
                    label={label}
                    // Merged, not replaced: Autocomplete's own blur handler closes the popup.
                    inputProps={{
                        ...params.inputProps,
                        onBlur: event => {
                            params.inputProps.onBlur?.(event)
                            commitTypedValue()
                        },
                    }}
                />
            )}
        />
    )
}

export default MultipleTextInput
